import { useInfiniteQuery } from 'react-query';
import { useRouter } from 'next/router';

import {
  useOAIInfiniteQuery,
  useOAIMutation,
  useOAIQuery,
} from '~/hooks/reactQuery';
import { useRouterQueryByKey } from '~/hooks/url';
import { fetchOAIData } from '~/libs/openapi';
import type { OAIParameters, OAISchema } from '~/types/openapi';

import { getSnowdonJwt } from '../auth/utils';

type ProductTotalCountOptions = {
  enabled?: boolean;
  variables?: OAIParameters<'/api/market-place/v3/products/count', 'get'>;
};

const useProductTotalCount = (options?: ProductTotalCountOptions) => {
  const { data: totalCount } = useOAIQuery({
    queryKey: '/api/market-place/v3/products/count',
    variables: options?.variables,
    queryOptions: {
      ...options,
      suspense: false,
      keepPreviousData: true,
    },
  });

  return totalCount;
};

export const useInfiniteBuyerCouponProducts = ({
  buyerCouponId,
  sortType,
  size = 20,
  queryOptions = {
    refetchOnWindowFocus: true,
  },
}: {
  buyerCouponId: number;
  sortType: string;
  size?: number;
  queryOptions?: Record<string, unknown>;
}) => {
  const variables = { buyerCouponId, sortType, size };

  const query = useInfiniteQuery(
    ['/api/market-place/v1/buyer-coupon/products', buyerCouponId, sortType],
    async ({ signal, pageParam }) => {
      const data = await fetchOAIData<OAISchema['BuyerCouponProductResponse']>({
        queryKey: '/api/market-place/v1/buyer-coupon/products',
        variables: {
          ...variables,
          ...((pageParam || {}) as Record<string, unknown>),
        },
        fetchOptions: {
          signal,
          headers: {
            'X-Client-Auth-Token': getSnowdonJwt(),
          },
        },
      });

      return {
        ...data,
        count: data.totalCount,
        products: data.products,
      };
    },
    {
      ...queryOptions,
      keepPreviousData: true,
      getNextPageParam: (params) => {
        // eslint-disable-next-line @typescript-eslint/no-shadow
        const { hasNextPage, sortType, searchAfter } = params.products;
        if (!hasNextPage) {
          return undefined;
        }

        return { sortType, searchAfter };
      },
    },
  );

  const fetchNextPage = () => {
    if (query.isFetching || !query.hasNextPage) {
      return;
    }

    void query.fetchNextPage();
  };

  const pages = query.data?.pages.reduce(
    (acc, current) => acc.concat(current.products?.content),
    [] as OAISchema['MarketSelectProductResponseV2'][],
  );

  return {
    ...query,
    count: query.data?.pages[0].totalCount,
    pages,
    fetchNextPage,
  };
};

type ProductsOptions = {
  enabled?: boolean;
  variablesFromParameter?: OAIParameters<
    '/api/market-place/v3/products',
    'get'
  >;
};

export const useMarketProducts = (
  { enabled, variablesFromParameter }: ProductsOptions = {
    enabled: true,
    variablesFromParameter: {},
  },
) => {
  const router = useRouter();

  const variablesFromQuery: OAIParameters<
    '/api/market-place/v3/products',
    'get'
  > = {
    ...router.query,
  };

  const displayCategoryIds = (() => {
    if (router.query.fourDepthCategoryId) {
      return [router.query.fourDepthCategoryId];
    }

    if (router.query.threeDepthCategoryId) {
      return [router.query.threeDepthCategoryId];
    }

    if (router.query.twoDepthId) {
      return [router.query.twoDepthId];
    }
    return null;
  })();

  const variables = {
    ...variablesFromQuery,
    ...variablesFromParameter,
    ...(displayCategoryIds && { displayCategoryIds }),
  };

  const totalCount = useProductTotalCount({
    variables: variables as OAIParameters<
      '/api/market-place/v3/products/count',
      'get'
    >,
  });

  const infiniteQuery = useOAIInfiniteQuery<
    '/api/market-place/v3/products',
    OAISchema['MarketSelectProductResponseV2']
  >({
    queryKey: '/api/market-place/v3/products',
    variables,
    queryOptions: {
      enabled,
      keepPreviousData: true,
      getNextPageParam: ({ hasNextPage, searchAfter, sortType }) => {
        if (!hasNextPage) {
          return undefined;
        }

        return { searchAfter, sortType };
      },
    },
  });

  return {
    total: totalCount,
    ...infiniteQuery,
  };
};

export const useRecentlyViewedProducts = () => {
  const { data } = useOAIQuery({
    queryKey: '/api/market-place/v1/products/recently-viewed-products',
  });

  return data;
};

export const useCreateProductInquire = () =>
  useOAIMutation({
    method: 'post',
    mutationKey: '/api/market-place/v1/product-inquiries',
  });

export const useProductInquiries = () =>
  useOAIQuery({
    queryKey: '/api/market-place/v1/product-inquiries',
  });

export const useEditProductInquire = () =>
  useOAIMutation({
    method: 'put',
    mutationKey: '/api/market-place/v1/product-inquiries/{inquiryId}',
  });

export const useDeleteProductInquire = () =>
  useOAIMutation({
    method: 'delete',
    mutationKey: '/api/market-place/v1/product-inquiries/{inquiryId}',
  });

export const useInfiniteMyInquiries = () => {
  const infiniteQuery = useOAIInfiniteQuery<
    '/api/market-place/v1/product-inquiries',
    OAISchema['MarketPlaceProductInquiryResponse']
  >({
    queryKey: '/api/market-place/v1/product-inquiries',
    queryOptions: {
      keepPreviousData: true,
      getNextPageParam: ({ hasNextPage, lastId }) => {
        if (!hasNextPage) {
          return undefined;
        }

        return { lastInquiryId: lastId };
      },
    },
  });

  return {
    ...infiniteQuery,
  };
};
export const useInfiniteProductInquiries = (offerId: number) => {
  const infiniteQuery = useOAIInfiniteQuery<
    '/api/market-place/v1/product-inquiries/{offerId}',
    OAISchema['MarketPlaceProductInquiryResponse']
  >({
    queryKey: '/api/market-place/v1/product-inquiries/{offerId}',
    variables: {
      offerId,
    },
    queryOptions: {
      enabled: Boolean(offerId),
      keepPreviousData: true,
      getNextPageParam: ({ hasNextPage, lastId }) => {
        if (!hasNextPage) {
          return undefined;
        }

        return { lastInquiryId: lastId };
      },
    },
  });

  return {
    ...infiniteQuery,
  };
};

export const useMyInquiryCount = () => {
  const { data } = useOAIQuery({
    queryKey: '/api/market-place/v1/product-inquiries/count',
  });

  return data || 0;
};

export const useBestProducts = () => {
  const { data } = useOAIQuery({
    queryKey: '/api/market-place/v4/showcases',
    variables: {
      code: 'cart_best',
    },
    queryOptions: {
      keepPreviousData: true,
    },
  });

  return data;
};

/**
 * 자주 구매하는 상품 리스트
 * */
export const useFrequentlyOrderProducts = () =>
  useOAIQuery({
    queryKey: '/api/market-place/v1/frequently-order/products',
    queryOptions: {
      keepPreviousData: true,
      suspense: true,
    },
  });

export const useEstimateProductPayment = () =>
  useOAIMutation({
    method: 'post',
    mutationKey: '/api/market-place/v2/products/estimate-product-payment',
  });

export const useMarketProductDetail = (parameterOfferId?: number) => {
  const routerOfferId = useRouterQueryByKey('offerId', 'number');
  const offerId = parameterOfferId || routerOfferId;

  const { data, ...props } = useOAIQuery({
    queryKey: '/api/market-place/v2/products/{offerId}/details',
    variables: { offerId },
    queryOptions: {
      enabled: !!Number(offerId),
    },
  });

  return {
    data: data as OAISchema['MarketProductDetailsResponseV2'],
    ...props,
  };
};

/**
 * @description 상품 옵션 조회
 * */
export const useMarketProductDetailOptions = (offerId: number) => {
  return useOAIQuery({
    queryKey: '/api/market-place/v2/products/{offerId}/options',
    variables: { offerId },
    queryOptions: {
      enabled: offerId !== 0 && !!Number(offerId),
      retry: 2,
    },
  });
};

/**
 * @description
 * 상품 상세 - 함께 구매하는 상품 목록 조회
 * */
export const useMarketProductCrossSellProductList = (
  offerId: number,
  enabled = false,
) => {
  const { data, ...props } = useOAIQuery({
    queryKey: '/api/market-place/v1/cross-sell/{offerId}/products',
    variables: { offerId },
    queryOptions: {
      enabled,
    },
  });

  return {
    data,
    ...props,
  };
};

/**
 * @description
 * 상품 상세 - 유사상품 목록 조회
 * */
export const useSimilarProducts = (parameterOfferId?: number) => {
  const routerOfferId = useRouterQueryByKey('offerId', 'number');
  const offerId = parameterOfferId || routerOfferId;

  return useOAIQuery({
    queryKey: '/api/market-place/v1/similar/products',
    variables: { offerId },
    queryOptions: {
      enabled: !!Number(offerId),
      keepPreviousData: true,
    },
  });
};
